import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, AlertCircle, Send } from "lucide-react";
import { api, apiError } from "@/lib/api";
import Seo from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { CITIES, PROPERTY_TYPES } from "@/lib/format";

const EMPTY = { name: "", phone: "", email: "", purpose: "buy", city: "", ptype: "", budget: "", message: "", website: "" };
const PHONE_RE = /^[0-9+\s\-()]{8,18}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const BUDGETS_BUY = ["أقل من مليون ريال", "1 – 3 ملايين ريال", "3 – 5 ملايين ريال", "أكثر من 5 ملايين ريال"];
const BUDGETS_RENT = ["أقل من 60 ألف سنوياً", "60 – 120 ألف سنوياً", "120 – 200 ألف سنوياً", "أكثر من 200 ألف سنوياً"];

export default function RequestProperty() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [state, setState] = useState("idle"); // idle | sending | done | duplicate
  const [refId, setRefId] = useState("");
  const [serverError, setServerError] = useState("");

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "يرجى إدخال الاسم الكامل";
    if (!PHONE_RE.test(form.phone.trim())) e.phone = "يرجى إدخال رقم جوال صحيح (مثال: 05xxxxxxxx)";
    if (form.email && !EMAIL_RE.test(form.email.trim())) e.email = "صيغة البريد الإلكتروني غير صحيحة";
    if (!form.city) e.city = "يرجى اختيار المدينة";
    if (!form.ptype) e.ptype = "يرجى اختيار نوع العقار";
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (state === "sending" || state === "done") return;
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setState("sending");
    setServerError("");
    try {
      const r = await api.post("/property-requests", { ...form, email: form.email || null, budget: form.budget || null, message: form.message || null });
      setRefId(r.data.id?.slice(0, 8) || "");
      setState("done");
    } catch (err) {
      if (err?.response?.status === 409) setState("duplicate");
      else {
        setServerError(apiError(err));
        setState("idle");
      }
    }
  };

  const budgets = form.purpose === "rent" ? BUDGETS_RENT : BUDGETS_BUY;

  return (
    <>
      <Seo title="اطلب عقارك" description="أرسل مواصفات العقار الذي تبحث عنه — نموذج طلب تجريبي ضمن موقع وساطة عقارية افتراضي." />
      <section className="bg-forest py-16 md:py-24">
        <div className="container-lux space-y-4">
          <p className="text-gold text-sm font-heading font-semibold tracking-widest">خدمة طلب عقار</p>
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-ivory tracking-tight">أخبرنا عمّا تبحث عنه</h1>
          <p className="text-ivory/70 max-w-xl">املأ النموذج وسنقوم بترشيح عقارات تناسب متطلباتك. بيانات هذا النموذج تجريبية ولأغراض العرض.</p>
        </div>
      </section>

      <section className="container-lux py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-7">
            {state === "done" ? (
              <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="rounded-3xl bg-white p-10 shadow-[0_20px_60px_rgba(10,42,26,0.08)] text-center space-y-5" data-testid="request-success">
                <CheckCircle2 className="h-16 w-16 text-forest mx-auto" aria-hidden="true" />
                <h2 className="font-heading font-bold text-2xl text-forest">تم استلام طلبك بنجاح</h2>
                <p className="text-charcoal/70 leading-relaxed">
                  شكراً {form.name}. رقم الطلب المرجعي: <span className="font-heading font-bold text-gold-dark" dir="ltr" data-testid="request-ref-id">{refId}</span>
                </p>
                <p className="text-xs text-muted-foreground">هذا نموذج تجريبي — لن يتم التواصل فعلياً بناءً على هذا الطلب.</p>
                <Link to="/properties" className="btn-gold inline-flex" data-testid="success-browse-link">تصفح العقارات</Link>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="rounded-3xl bg-white p-8 md:p-10 shadow-[0_20px_60px_rgba(10,42,26,0.08)] space-y-6" data-testid="request-form" noValidate>
                {state === "duplicate" && (
                  <div className="flex items-start gap-3 rounded-xl border border-gold/40 bg-gold/10 p-4 text-sm text-charcoal" data-testid="request-duplicate-notice">
                    <AlertCircle className="h-5 w-5 text-gold-dark shrink-0" aria-hidden="true" />
                    تم استلام طلب مسبق بنفس رقم الجوال خلال آخر 24 ساعة — لا حاجة لإعادة الإرسال.
                  </div>
                )}
                {serverError && (
                  <div className="rounded-xl bg-destructive/10 text-destructive px-5 py-4 text-sm" data-testid="request-server-error">{serverError}</div>
                )}

                <input type="text" name="website" value={form.website} onChange={(e) => set("website", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <label htmlFor="req-name" className="text-sm font-heading font-semibold text-forest">الاسم الكامل *</label>
                    <input id="req-name" className="input-lux" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="مثال: محمد العتيبي" data-testid="request-name-input" />
                    {errors.name && <p className="text-xs text-destructive" data-testid="request-name-error">{errors.name}</p>}
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="req-phone" className="text-sm font-heading font-semibold text-forest">رقم الجوال *</label>
                    <input id="req-phone" className="input-lux" dir="ltr" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="05xxxxxxxx" data-testid="request-phone-input" />
                    {errors.phone && <p className="text-xs text-destructive" data-testid="request-phone-error">{errors.phone}</p>}
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="req-email" className="text-sm font-heading font-semibold text-forest">البريد الإلكتروني (اختياري)</label>
                    <input id="req-email" className="input-lux" dir="ltr" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="name@email.com" data-testid="request-email-input" />
                    {errors.email && <p className="text-xs text-destructive" data-testid="request-email-error">{errors.email}</p>}
                  </div>
                  <div className="space-y-2">
                    <span className="text-sm font-heading font-semibold text-forest">الغرض *</span>
                    <div className="flex gap-3 pt-1" role="radiogroup" aria-label="الغرض">
                      {[["buy", "شراء"], ["rent", "إيجار"]].map(([v, label]) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => set("purpose", v)}
                          aria-pressed={form.purpose === v}
                          data-testid={`request-purpose-${v}`}
                          className={`flex-1 rounded-full border px-5 py-2.5 text-sm font-heading font-semibold transition-colors ${
                            form.purpose === v ? "border-forest bg-forest text-ivory" : "border-forest/20 text-forest hover:border-gold"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="req-city" className="text-sm font-heading font-semibold text-forest">المدينة *</label>
                    <select id="req-city" className="input-lux" value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="request-city-select">
                      <option value="">اختر المدينة</option>
                      {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    {errors.city && <p className="text-xs text-destructive" data-testid="request-city-error">{errors.city}</p>}
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="req-type" className="text-sm font-heading font-semibold text-forest">نوع العقار *</label>
                    <select id="req-type" className="input-lux" value={form.ptype} onChange={(e) => set("ptype", e.target.value)} data-testid="request-type-select">
                      <option value="">اختر النوع</option>
                      {PROPERTY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    {errors.ptype && <p className="text-xs text-destructive" data-testid="request-type-error">{errors.ptype}</p>}
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="req-budget" className="text-sm font-heading font-semibold text-forest">الميزانية التقريبية (اختياري)</label>
                  <select id="req-budget" className="input-lux" value={form.budget} onChange={(e) => set("budget", e.target.value)} data-testid="request-budget-select">
                    <option value="">اختر الميزانية</option>
                    {budgets.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>

                <div className="space-y-2">
                  <label htmlFor="req-message" className="text-sm font-heading font-semibold text-forest">متطلبات إضافية (اختياري)</label>
                  <textarea id="req-message" rows={4} className="input-lux resize-none" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="مثال: أبحث عن فيلا بحي شمالي قريبة من المدارس، مع مسبح…" data-testid="request-message-input" />
                </div>

                <button type="submit" disabled={state === "sending"} className="btn-gold w-full disabled:opacity-60 disabled:translate-y-0" data-testid="request-submit-button">
                  {state === "sending" ? "جارٍ الإرسال…" : "إرسال الطلب"}
                  <Send className="h-4 w-4" aria-hidden="true" />
                </button>
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  بإرسالك هذا النموذج التجريبي فأنت تقر بأن البيانات ستُستخدم لأغراض العرض التقديمي فقط ولن تتم مشاركتها مع أي طرف.
                </p>
              </form>
            )}
          </Reveal>

          <div className="lg:col-span-5 space-y-6">
            <Reveal delay={0.1}>
              <div className="rounded-3xl bg-forest p-8 text-ivory space-y-5">
                <h2 className="font-heading font-bold text-xl">كيف تعمل الخدمة؟</h2>
                <ol className="space-y-4 text-sm text-ivory/80">
                  {["أرسل مواصفات العقار المطلوب", "يراجع مستشارك الطلب ويرشّح الخيارات", "نساعدك في المعاينة والتفاوض حتى الإتمام"].map((s, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-gold font-heading font-bold text-xs text-forest">{i + 1}</span>
                      {s}
                    </li>
                  ))}
                </ol>
              </div>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="rounded-3xl border border-gold/25 bg-white p-8 space-y-3">
                <Eyebrow>خصوصيتك أولاً</Eyebrow>
                <p className="text-sm text-charcoal/75 leading-relaxed">
                  هذا النموذج جزء من موقع تجريبي: لا تُرسل البيانات لأي جهة تسويقية، وتُحذف البيانات التجريبية دورياً.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
